// #479: the wizard's options a model may admit (ProgramModel.options): a
// comeback after weeks off, and one muscle to bring up. Each writes into the
// model's rules before the fit, as the added exercises do (programModelExtras.ts).
// Keep free of React and any UI import.
import { CATALOG_MUSCLE_SLUGS, type CatalogMuscleSlug } from "./muscleCatalog.js";
import { modelRowEntry, modelRunDays, type ModelLevel, type ModelRow, type ProgramModel } from "./programModel.js";
import {
  fitModelDraft,
  mapRuleValues,
  modelRowExercise,
  oneSetFewer,
  ruleToRpe,
  type ModelChoices,
} from "./programModelDraft.js";
import type { ProgressionRule } from "./programProgression.js";
import type { Exercise } from "./schemas/exercise.js";
import { MAX_ROUND_SETS } from "./schemas/programSlot.js";
import { PROGRAM_FOCUS } from "./schemas/programV3.js";
import { resolveExerciseMuscleCredits } from "./volumeByMuscle.js";

/** The weeks off a comeback offers. The last means that many or more. */
export const LAYOFF_WEEKS = [2, 4, 8, 12] as const;
export type LayoffWeeks = (typeof LAYOFF_WEEKS)[number];

/** «8», and «12+» on the last value: that many weeks or more. */
export const layoffLabel = (w: LayoffWeeks): string =>
  w === LAYOFF_WEEKS[LAYOFF_WEEKS.length - 1] ? `${w}+` : String(w);

/** What the athlete chose. An option the model does not admit is dropped. */
export type ModelOptionChoices = {
  comeback?: { layoffWeeks: LayoffWeeks };
  specialization?: { muscle: CatalogMuscleSlug };
};

type Model = Omit<ProgramModel, "id">;
type ModelEntry = NonNullable<ModelRow["byLevel"][ModelLevel]>;
type At = Pick<ModelChoices, "exercises" | "exercisePicks">;

/** The options of `chosen` that `model` admits. */
export const admittedOptions = (
  model: Pick<Model, "options">,
  chosen: ModelOptionChoices | undefined,
): ModelOptionChoices => ({
  ...(chosen?.comeback && model.options?.comeback ? { comeback: chosen.comeback } : {}),
  ...(chosen?.specialization && model.options?.specialization ? { specialization: chosen.specialization } : {}),
});

/** RPE points off every effort, and one set fewer, by weeks off. */
export const COMEBACK_EASE: Readonly<Record<LayoffWeeks, { rpe: number; setFewer: boolean }>> = {
  2: { rpe: 0.5, setFewer: false },
  4: { rpe: 1, setFewer: false },
  8: { rpe: 1.5, setFewer: true },
  12: { rpe: 2, setFewer: true },
};

/** The ease never takes an effort under this RPE. One already under stays. */
const COMEBACK_RPE_FLOOR = 5;

/** `rule` after `weeks` off, in RPE: every effort lower, never under the
 *  floor, and one set fewer from 8 weeks. The steps stay, so the climb is
 *  the model's. Another axis keeps its values. */
export const comebackRule = (rule: ProgressionRule, weeks: LayoffWeeks): ProgressionRule => {
  const { rpe, setFewer } = COMEBACK_EASE[weeks];
  const ease = (x: number) => Math.min(x, Math.max(COMEBACK_RPE_FLOOR, x - rpe));
  let r = ruleToRpe(rule);
  if (r.axis === "rpe") r = mapRuleValues(r, "rpe", ease, (x) => x);
  const effort = r.deload?.effort;
  if (effort) r = { ...r, deload: { ...r.deload, effort: { ...effort, value: ease(effort.value) } } };
  return setFewer ? oneSetFewer(r) : r;
};

/** One set more, up to the round's cap. The new set takes the last per-set
 *  offset, and a threshold's set ceiling goes up with it. */
export const oneSetMore = (rule: ProgressionRule): ProgressionRule => {
  const sets = Math.min(MAX_ROUND_SETS, rule.sets + 1);
  if (sets === rule.sets) return rule;
  const { threshold } = rule;
  return {
    ...rule,
    sets,
    ...(threshold?.setsMax !== undefined
      ? { threshold: { ...threshold, setsMax: Math.min(MAX_ROUND_SETS, threshold.setsMax + 1) } }
      : {}),
  };
};

/** The muscles an exercise works directly: a full hard set each (#538). */
const directMuscles = (ex: Exercise | undefined): CatalogMuscleSlug[] =>
  ex ? resolveExerciseMuscleCredits(ex).flatMap(([m, credit]) => (credit >= 1 ? [m] : [])) : [];

type Runs = Pick<Model, "options" | "levels" | "days" | "daysBySessions" | "sessionsPerWeek">;

/** The muscles a specialization offers: those a row's default exercise works
 *  directly, at any level and focus, on a day that runs. Empty when the model
 *  does not admit it, or without the catalog. */
export const modelSpecialMuscles = (
  model: Runs,
  exercises: ReadonlyMap<string, Exercise> | undefined,
): CatalogMuscleSlug[] => {
  if (!model.options?.specialization || !exercises) return [];
  const found = new Set<CatalogMuscleSlug>();
  for (const row of modelRunDays(model).flatMap((d) => d.rows)) {
    for (const level of model.levels) {
      for (const focus of [undefined, ...PROGRAM_FOCUS]) {
        const slug = modelRowEntry(row, level, focus)?.entry.exercises[0];
        for (const m of directMuscles(slug === undefined ? undefined : exercises.get(slug))) found.add(m);
      }
    }
  }
  return CATALOG_MUSCLE_SLUGS.filter((m) => found.has(m));
};

type RuleOf = (entry: ModelEntry) => ProgressionRule;

const onLevels = <E extends ModelEntry>(byLevel: Partial<Record<ModelLevel, E>>, f: RuleOf) =>
  Object.fromEntries(
    Object.entries(byLevel).map(([level, e]) => [level, e && { ...e, rule: f(e) }]),
  ) as Partial<Record<ModelLevel, E>>;

/** `row` with every entry's rule through `f`, at each level and focus. */
const rowRules = (row: ModelRow, f: RuleOf): ModelRow => ({
  ...row,
  byLevel: onLevels(row.byLevel, f),
  ...(row.byFocus
    ? {
        byFocus: Object.fromEntries(
          Object.entries(row.byFocus).map(([focus, byLevel]) => [focus, byLevel && onLevels(byLevel, f)]),
        ) as ModelRow["byFocus"],
      }
    : {}),
});

/** `model` with the options it admits written into its rules: a comeback
 *  eases every rule (comebackRule); a specialization adds one set to each
 *  row whose exercise (the pick, else the default) works the muscle directly. */
export const modelWithOptions = <M extends Model>(model: M, chosen: ModelOptionChoices | undefined, at: At): M => {
  const { comeback, specialization } = admittedOptions(model, chosen);
  if (!comeback && !specialization) return model;
  const ruleIn = (row: ModelRow): RuleOf => (e) => {
    const rule = comeback ? comebackRule(e.rule, comeback.layoffWeeks) : e.rule;
    const ex = at.exercises?.get(modelRowExercise(e, at.exercisePicks?.[row.id]));
    return specialization && directMuscles(ex).includes(specialization.muscle) ? oneSetMore(rule) : rule;
  };
  return { ...model, days: model.days.map((day) => ({ ...day, rows: day.rows.map((row) => rowRules(row, ruleIn(row))) })) };
};

/** fitModelDraft with the options written in. `ignoreOldMax`: a comeback is
 *  on, so the start is today's effort, not the old MAX nor the lifts before
 *  the break. The draft cannot carry it: no program field holds it, and the
 *  logger still proposes from history. The caller says it to the athlete. */
export const fitModelWithOptions = (model: Model, choices: ModelChoices, options: ModelOptionChoices | undefined) => ({
  ...fitModelDraft(modelWithOptions(model, options, choices), choices),
  ignoreOldMax: admittedOptions(model, options).comeback !== undefined,
});
